/**
 * Operation Mode — human resistance gauge + DNA economy (Plague-style).
 * The resistance gauge is the core new tension: as infiltration becomes exposed,
 * humanity (MJ-12) researches a "purge". Reach 100 first and the operation fails.
 * Pure & deterministic — no DOM, no Math.random.
 */

import { REGIONS, getRegion } from '../data/regions.js';
import type { EvoEffects } from '../data/evolution.js';
import type { OperationDifficulty } from '../state.js';

/** Base resistance growth per exposure-unit per tick. */
export const BASE_RESIST = 0.16;
/** Wealthy regions accelerate resistance research when infiltrated. */
export const WEALTH_K = 0.28;
/** DNA generated per population-weighted infiltration unit per tick. */
export const DNA_K = 0.32;
/** Resistance is complete (loss) at this value. */
export const RESISTANCE_MAX = 100;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Difficulty multiplier applied to resistance growth. */
export function difficultyResistMul(difficulty: OperationDifficulty): number {
  switch (difficulty) {
    case 'easy': return 0.6;
    case 'normal': return 1;
    case 'brutal': return 1.5;
    case 'impossible': return 2.2;
  }
}

/**
 * Exposure drives resistance. Wealthy infiltrated regions weigh more (they fund
 * research faster). Dominance aggression adds exposure; stealth reduces it.
 */
export function computeExposure(
  infection: Record<string, number>,
  effects: EvoEffects,
): number {
  let weighted = 0;
  for (const region of REGIONS) {
    const inf = (infection[region.id] ?? 0) / 100;
    weighted += inf * (1 + (region.wealth - 1) * WEALTH_K);
  }
  const exposure = weighted + effects.exposureAdd - effects.stealthReduce;
  return Math.max(0, exposure);
}

/** Advance the resistance gauge one tick. Returns the new value (0..100). */
export function stepResistance(
  resistance: number,
  infection: Record<string, number>,
  effects: EvoEffects,
  difficulty: OperationDifficulty,
): number {
  const exposure = computeExposure(infection, effects);
  const growth = exposure * BASE_RESIST * difficultyResistMul(difficulty) * effects.resistanceSlow;
  return clamp(resistance + growth, 0, RESISTANCE_MAX);
}

/** Loss: humanity's purge research is complete. */
export function resistanceComplete(resistance: number): boolean {
  return resistance >= RESISTANCE_MAX;
}

/**
 * DNA generated this tick: population-weighted infiltration, scaled by dominance
 * depth and DNA multipliers, plus any flat DNA from evolution.
 */
export function dnaPerTick(
  infection: Record<string, number>,
  effects: EvoEffects,
): number {
  let popUnits = 0;
  for (const region of REGIONS) {
    const inf = (infection[region.id] ?? 0) / 100;
    popUnits += inf * region.population;
  }
  return popUnits * DNA_K * effects.dnaMul * effects.dominanceMul + effects.dnaFlat;
}

/** DNA awarded by collecting a bubble: a burst proportional to current spread. */
export function bubbleDnaAmount(infection: Record<string, number>): number {
  let popUnits = 0;
  for (const region of REGIONS) {
    const inf = (infection[region.id] ?? 0) / 100;
    popUnits += inf * region.population;
  }
  return Math.max(3, Math.round(popUnits * 1.5 + 3));
}

/** Region with the highest infiltration (for bubble placement). Deterministic. */
export function mostInfiltratedRegion(infection: Record<string, number>): string {
  let bestId = REGIONS[0].id;
  let best = -1;
  for (const region of REGIONS) {
    const inf = infection[region.id] ?? 0;
    if (inf > best) { best = inf; bestId = region.id; }
  }
  return bestId;
}

/** Re-export for callers that only need region lookups alongside these systems. */
export { getRegion };
